"use client";

import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { RichTextInput, type RichTextInputHandle } from "./RichTextInput";
import { EmojiPicker } from "./EmojiPicker";
import { AttachmentTray, AddAttachmentButton } from "./AttachmentTray";
import { updatePost } from "@/lib/api/posts";
import { useAttachments } from "@/lib/hooks/useAttachments";
import { useBodyScrollLock } from "@/lib/hooks/useBodyScrollLock";
import type { Attachment } from "@theta/api-client";

/**
 * Edit a post: its text, and add/remove/reorder of what is attached to it.
 *
 * Seeded from the DOCUMENT — `bodyMarkdown` plus `attachments` rows — rather
 * than from a body with `![name|800x600](upload://…)` tokens in it that had to
 * be parsed out on the way in and rebuilt on the way out (§1.12).
 */
export function PostEditor({
  postId,
  initialText,
  initialAttachments,
  groupId,
  groupSlug,
  onClose,
  onSaved,
}: {
  postId: number;
  initialText: string;
  initialAttachments: Attachment[];
  /** Group id — powers @-mentions, the group's custom emojis, and upload scope. */
  groupId: number;
  /** The group's slug, for the @alle capability lookup (OSO-681). */
  groupSlug?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [body, setBody] = useState(initialText);
  const [showFormatting, setShowFormatting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const mentionRef = useRef<RichTextInputHandle>(null);
  const files = useAttachments({ type: "group", id: groupId }, initialAttachments);

  useBodyScrollLock();

  const canSave =
    (body.trim().length > 0 || files.hasContent) && !files.isUploading && !saving;
  const error = saveError ?? files.error;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updatePost(postId, {
        bodyMarkdown: mentionRef.current?.getRaw() ?? body,
        attachments: files.uploadIds,
      });
      onSaved();
    } catch {
      setSaveError("Opslaget kunne ikke gemmes. Prøv igen.");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-surface rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
        <div className="border-b border-border py-4 px-4 text-center relative shrink-0">
          <h2 className="font-bold text-xl text-foreground">
            Rediger opslag
          </h2>
          <button
            onClick={onClose}
            aria-label="Luk"
            className="absolute right-3 top-3 w-9 h-9 rounded-full bg-muted hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-muted-foreground transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <RichTextInput
            ref={mentionRef}
            ariaLabel="Redigér opslaget"
            value={body}
            onChange={setBody}
            placeholder="Skriv dit opslag..."
            className="w-full text-foreground bg-transparent focus:outline-none text-content leading-relaxed"
            multiline
            minHeight={80}
            autoResize
            autoFocus
            groupId={groupId}
            groupSlug={groupSlug}
            hideMentionAll
            onPasteFiles={files.addFiles}
            onSubmit={handleSave}
            hideFormattingIcon
            formattingOpen={showFormatting}
            onFormattingOpenChange={setShowFormatting}
          />

          {files.attachments.length > 0 && (
            <AttachmentTray
              attachments={files.attachments}
              onRemove={files.remove}
              onMove={files.move}
            />
          )}

          {error && (
            <p className="mt-3 text-xs text-destructive">{error}</p>
          )}
        </div>

        <div className="border-t border-border p-4 shrink-0">
          <div className="flex items-center gap-1 mb-3 border border-border rounded-lg px-3 py-2">
            <span className="text-sm font-medium text-foreground flex-1">
              {files.attachments.length > 0 ? "Rediger vedhæftninger" : "Tilføj til dit opslag"}
            </span>

            <AddAttachmentButton onFiles={files.addFiles} label="Tilføj foto eller video" />
            <AddAttachmentButton
              onFiles={files.addFiles}
              accept="*/*"
              label="Vedhæft fil"
              className="w-9 h-9 rounded-full hover:bg-blue-50 dark:hover:bg-gray-800 flex items-center justify-center text-blue-500 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </AddAttachmentButton>

            <EmojiPicker
              groupId={groupId}
              canAdd={false}
              onSelect={(text) => {
                setBody((prev) => {
                  const needsSpace =
                    prev.length > 0 && !prev.endsWith(" ") && !prev.endsWith("\n");
                  return prev + (needsSpace ? " " : "") + `${text} `;
                });
              }}
            />

            <button
              type="button"
              onClick={() => setShowFormatting((v) => !v)}
              aria-pressed={showFormatting}
              aria-label="Formatering"
              title="Formatering"
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                showFormatting
                  ? "bg-primary-muted text-primary-muted-foreground"
                  : "hover:bg-muted text-muted-foreground"
              }`}
            >
              Aa
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-subtle-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Gemmer..." : files.isUploading ? "Uploader..." : "Gem"}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold text-foreground bg-muted hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Annuller
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
